import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, Shield, Download } from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import whitepaperMd from '../content/legal/RITHMO_SECURITY_AI_PRIVACY_WHITEPAPER.md?raw'
import { usePageMeta } from '../hooks/usePageMeta'

export const SECURITY_WHITEPAPER_LAST_UPDATED = 'May 2026'

const WEB_WHITEPAPER_MD = whitepaperMd.replace(/^#\s+[^\n]+\n+/, '')

const whMarkdownComponents = {
  h2: ({ children }) => (
    <div className="mt-14 mb-5 pt-10 border-t border-gray-200">
      <h2 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
        <span className="flex-shrink-0 w-1 h-7 rounded-full bg-gradient-to-b from-primary-500 to-accent-500" />
        {children}
      </h2>
    </div>
  ),
  h3: ({ children }) => (
    <h3 className="mt-8 mb-3 pl-4 border-l-2 border-accent-500 text-lg font-semibold text-gray-900">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="mb-4 text-gray-600 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-4 space-y-2">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 space-y-2 list-decimal list-inside marker:text-accent-500 marker:font-semibold">
      {children}
    </ol>
  ),
  li: ({ children, ordered }) => (
    ordered ? (
      <li className="text-gray-600 leading-relaxed pl-1">{children}</li>
    ) : (
      <li className="flex items-start gap-2.5 text-gray-600 leading-relaxed">
        <CheckCircle className="w-4 h-4 text-accent-500 flex-shrink-0 mt-0.5" />
        <span>{children}</span>
      </li>
    )
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  table: (props) => (
    <div className="overflow-x-auto my-8 rounded-xl border border-gray-200 shadow-sm">
      <table className="min-w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: (props) => (
    <thead className="bg-gradient-to-r from-primary-50 to-accent-50" {...props} />
  ),
  th: (props) => (
    <th
      className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top"
      {...props}
    />
  ),
  td: (props) => (
    <td
      className="px-4 py-3.5 text-sm text-gray-600 border-b border-gray-100 align-top"
      {...props}
    />
  ),
  tr: (props) => (
    <tr className="hover:bg-gray-50/60 transition-colors" {...props} />
  ),
}

function SecurityWhitepaperPage() {
  usePageMeta({
    title: 'Rithmo AI Security & Privacy Whitepaper',
    description: 'How Rithmo protects your meeting data. Covers end-to-end encryption, audio retention policy, AI data handling, and enterprise compliance commitments.',
  })
  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/60 via-white to-white" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-accent-500/8 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex justify-center mb-5">
              <div className="flex items-center gap-2 badge-primary px-4 py-2">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-semibold">Security & AI Privacy</span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Rithmo Security &amp; AI{' '}
              <span className="gradient-text">Data Privacy Whitepaper</span>
            </h1>

            <p className="text-gray-500 mb-6">Last updated: {SECURITY_WHITEPAPER_LAST_UPDATED}</p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 max-w-2xl mx-auto text-sm text-amber-900 leading-relaxed mb-8">
              This document is a draft for customer security, privacy, IT, procurement, and legal
              review. Reconcile it with the Privacy Policy, DPA, subprocessor list, and deployed
              technical controls before relying on it in procurement.
            </div>

            <Link to="/whitepaper-contact" className="btn-primary inline-flex items-center gap-2">
              <Download className="w-4 h-4" />
              Request Full Documentation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Document Body ─────────────────────────────────────────────────── */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Quick-reference summary cards */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid sm:grid-cols-3 gap-4 mb-14 p-6 rounded-2xl bg-gradient-to-br from-primary-50/60 to-accent-50/40 border border-accent-500/10"
          >
            {[
              { label: 'Encryption', value: 'AES-256 at rest · TLS 1.3 in transit' },
              { label: 'Audio Retention', value: 'Processed in real time · Never stored' },
              { label: 'Model Training', value: 'Customer data never used · Ever' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <p className="text-[10px] font-semibold text-accent-600 uppercase tracking-widest mb-1">{item.label}</p>
                <p className="text-sm font-medium text-gray-900">{item.value}</p>
              </div>
            ))}
          </motion.div>

          {/* Markdown content */}
          <motion.article
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={whMarkdownComponents}>
              {WEB_WHITEPAPER_MD}
            </ReactMarkdown>
          </motion.article>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 p-8 rounded-2xl bg-gradient-dark text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 60% 50%, rgba(22,193,178,0.15) 0%, transparent 60%)' }} />
            <div className="relative z-10">
              <h3 className="text-xl font-bold text-white mb-2">Need the Full Security Package?</h3>
              <p className="text-dark-200 mb-6 text-sm max-w-md mx-auto">
                Request the DPA, subprocessor list, AI processing summary, and security architecture overview for your procurement review.
              </p>
              <Link to="/whitepaper-contact" className="btn-primary inline-flex items-center gap-2">
                <Download className="w-4 h-4" />
                Request Documentation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default SecurityWhitepaperPage
